"use client";

import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { captureClientEvent } from "@/lib/analytics/client";
import { useCreatorReferralReward } from "@/hooks/useCreatorReferralReward";
import { useBoard } from "@/components/BoardProvider";
import { useAction } from "convex/react";
import { api } from "@repo/backend/convex/_generated/api";
import { EarlyBirdCountdown, EarlyBirdFlash } from "@/components/pricing/EarlyBirdCountdown";

export interface PricingModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  defaultTab?: "subscription" | "payg";
  trigger?: string;
  context?: Record<string, unknown>;
}

export function PricingModal({ open, setOpen, defaultTab = "subscription", trigger, context }: PricingModalProps) {
  const board = useBoard();
  const createCheckout = useAction(api.autumn.createCheckout);
  const {
    loading: creatorRewardLoading,
    shouldApply: shouldApplyCreatorReward,
    rewardId: creatorRewardId,
  } = useCreatorReferralReward();
  const closeReasonRef = useRef<"purchase" | null>(null);
  const hasTrackedOpenRef = useRef<boolean>(false);

  const checkoutRewardId =
    !creatorRewardLoading && shouldApplyCreatorReward && creatorRewardId
      ? creatorRewardId
      : undefined;

  const [pendingPlan, setPendingPlan] = useState<string | null>(null);

  const onChooseProduct = async (productId: string) => {
    if (pendingPlan) return;
    setPendingPlan(productId);
    closeReasonRef.current = "purchase";
    const ret = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : undefined;
    const successUrl = typeof window !== "undefined" && ret ? `${window.location.origin}/checkout/success?return=${ret}` : undefined;
    captureClientEvent("pricing_subscription_checkout_clicked", {
      productId,
      billingPeriod: productId === "pass_weekly" ? "weekly" : "lifetime",
      source: "pricing_modal",
    });
    try {
      const { paymentUrl } = await createCheckout({
        planId: productId,
        successUrl,
        rewardId: checkoutRewardId,
      });
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else if (successUrl) {
        // Applied without payment (e.g. instant upgrade) — go celebrate.
        window.location.href = successUrl;
      }
    } catch (e) {
      console.error("Checkout failed", e);
      setPendingPlan(null);
    }
    // On success we redirect away, so leave the button in its loading state.
  };

  useEffect(() => {
    if (open) {
      hasTrackedOpenRef.current = true;
      captureClientEvent("pricing_modal_opened", { defaultTab, source: "paywall", trigger, ...context });
      closeReasonRef.current = null;
    }
  }, [open, defaultTab, trigger, context]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && hasTrackedOpenRef.current) {
      captureClientEvent("pricing_modal_closed", { source: "paywall", trigger, ...context });
      if (closeReasonRef.current !== "purchase") {
        captureClientEvent("pricing_modal_dismissed", { source: "paywall", trigger, ...context });
      }
      closeReasonRef.current = null;
      hasTrackedOpenRef.current = false;
    }
    setOpen(nextOpen);
  };

  const allowSkip = Boolean(context && context.allowSkip);

  const handleSkip = () => {
    captureClientEvent("pricing_modal_skipped", { source: "paywall", trigger, ...context });
    setOpen(false);
  };

  const isTutorialMode = trigger === "tutorial";

  const features = (
    <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
      <li>• Every past paper, timed & marked</li>
      <li>• Unlimited question bank & adaptive practice</li>
      <li>• Worked solutions for every question</li>
      <li>• Full QOTD archive</li>
    </ul>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "flex max-h-[90vh] w-[92vw] flex-col gap-0 overflow-hidden p-0 text-foreground md:max-h-none",
          "sm:max-w-lg md:max-w-xl lg:max-w-3xl xl:max-w-4xl",
        )}
      >
        <div className="px-6 pt-5 pb-4 border-b bg-secondary/40 flex-shrink-0">
          <DialogTitle className="text-xl">
            {isTutorialMode ? "Lock in early-bird pricing" : "Unlock full access"}
          </DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {isTutorialMode
              ? `Every week before ${board.shortName} exam day counts — start with everything unlocked today, at prices that won't stay this low.`
              : `Everything you need for ${board.shortName}, unlocked today. When the timer hits zero it's £25/week and £150 lifetime.`}
          </p>
          <div className="mt-3 flex flex-col items-start gap-3 rounded-xl bg-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2.5">
              <EarlyBirdFlash onDark />
              <p className="text-sm font-bold text-white">
                Ends 15 Aug, 9am — <span className="text-white/75">then £25/week · £150 lifetime</span>
              </p>
            </div>
            <EarlyBirdCountdown size="sm" onDark />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-6 md:overflow-visible">
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2" role="list">
            <div
              className={cn(
                "border rounded-lg p-4 flex flex-col bg-card/40",
                isTutorialMode ? "border-dashed border-primary/25" : "",
              )}
              role="listitem"
            >
              <div>
                <h3 className="text-lg font-semibold">Exam Pass — Weekly</h3>
                <p className="text-sm text-muted-foreground mt-1">Everything, starting today.</p>
              </div>

              <div className="mt-4">
                <p className="text-2xl font-semibold">
                  <span className="mr-2 text-base font-normal text-muted-foreground line-through">£25</span>
                  £15/week
                </p>
                <p className="text-sm text-muted-foreground">Early-bird price · cancel anytime</p>
              </div>

              {features}

              <div className="mt-6 flex-1 flex items-end">
                <Button
                  variant="secondary"
                  className="w-full shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => onChooseProduct("pass_weekly")}
                  loading={pendingPlan === "pass_weekly"}
                  disabled={pendingPlan !== null}
                >
                  Start this week — £15
                </Button>
              </div>
            </div>

            <div
              className={cn(
                "border rounded-lg p-4 flex flex-col bg-card/40 border-primary/40 ring-1 ring-primary/30",
                isTutorialMode ? "bg-primary/5" : "",
              )}
              role="listitem"
            >
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">Exam Pass — Lifetime</h3>
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                    Best value
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  One payment, before the price goes up. Yours through exam day and beyond.
                </p>
              </div>

              <div className="mt-4">
                <p className="text-2xl font-semibold">
                  <span className="mr-2 text-base font-normal text-muted-foreground line-through">£150</span>
                  £90
                </p>
                <p className="text-sm text-muted-foreground">One-time payment · £60 off while early-bird lasts</p>
              </div>

              {features}

              <div className="mt-6 flex-1 flex items-end">
                <Button
                  className="w-full shadow-sm hover:shadow-md transition-shadow"
                  onClick={() => onChooseProduct("pass_lifetime")}
                  loading={pendingPlan === "pass_lifetime"}
                  disabled={pendingPlan !== null}
                >
                  Lock in £90 — lifetime
                </Button>
              </div>
            </div>
          </div>
        </div>

        {allowSkip ? (
          <div className="flex-shrink-0 px-6 pb-5 text-center">
            <button
              type="button"
              onClick={handleSkip}
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Skip for now
            </button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
